import { FormEvent, JSX, useState } from 'react';
import {
    useLogin,
    useNotify
} from 'react-admin';
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

export default function LoginForm(): JSX.Element {
    const [email, setEmail] = useState<string>("")
    const [password, setPassword] = useState<string>("");
    const [userMessage, setUserMessage] = useState<string>("");
    const [passMessage, setPassMessage] = useState<string>("");
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const login: any = useLogin();
    const notify: any = useNotify();

    const usernameHandler: (e: any) => void = (e: any): void => {
        setUserMessage("");
        setEmail(e.target.value);
    }

    const passwordHandler: (e: any) => void = (e: any): void => {
        setPassMessage("");
        setPassword(e.target.value);
    }

    const submitForm: (e: FormEvent) => void = (e: FormEvent): void => {
        e.preventDefault();

        if (email === "") {
            setUserMessage("Please enter a valid email");
        } else if (password === "") {
            setPassMessage("Please enter a valid password");
        } else {
            setLoading(true);
            login({ username: email, password })
                .catch((error: Error): any =>
                    // `||`, not `??`: an error carrying an empty message is exactly what used
                    // to leave the admin looking at a notification with nothing written in it.
                    notify(error?.message || 'Please enter a valid email or password', { type: 'error' })
                )
                .finally((): void => setLoading(false));
        }
    }

    return (
        <Box
            sx={ {
                minHeight: '100vh',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
                // Theme-driven so the page follows the app's light/dark setting.
                background: (theme) =>
                    `linear-gradient(160deg, ${ theme.palette.background.default } 0%, ${ theme.palette.action.hover } 100%)`
            } }
        >
            <Paper
                elevation={ 6 }
                sx={ {
                    width: '100%',
                    maxWidth: 420,
                    p: { xs: 3, sm: 4 },
                    borderRadius: 3
                } }
            >
                <Box
                    component="form"
                    onSubmit={ submitForm }
                    noValidate
                    aria-label="Sign in to Nok Nok HQ"
                >
                    <Stack spacing={ 3 }>
                        <Stack spacing={ 1.5 } alignItems="center">
                            <Avatar
                                src="/images/icon/nn-logo.png"
                                alt="Nok Nok"
                                variant="rounded"
                                sx={ { width: 88, height: 88 } }
                            />
                            <Typography variant="h5" component="h1" fontWeight={ 600 } textAlign="center">
                                Welcome To Nok Nok HQ
                            </Typography>
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                Sign in to continue
                            </Typography>
                        </Stack>

                        <Stack spacing={ 2.5 }>
                            <TextField
                                label="Email"
                                type="email"
                                value={ email }
                                onChange={ usernameHandler }
                                error={ userMessage !== "" }
                                helperText={ userMessage }
                                autoComplete="username"
                                autoFocus
                                fullWidth
                                disabled={ loading }
                            />
                            <TextField
                                label="Password"
                                type={ showPassword ? "text" : "password" }
                                value={ password }
                                onChange={ passwordHandler }
                                error={ passMessage !== "" }
                                helperText={ passMessage }
                                autoComplete="current-password"
                                fullWidth
                                disabled={ loading }
                                slotProps={ {
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={ (): void => setShowPassword(!showPassword) }
                                                    edge="end"
                                                    disabled={ loading }
                                                    aria-label={ showPassword ? "Hide password" : "Show password" }
                                                >
                                                    { showPassword ? <VisibilityOff /> : <Visibility /> }
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }
                                } }
                            />
                        </Stack>

                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            fullWidth
                            disabled={ loading }
                            startIcon={ loading ? <CircularProgress size={ 18 } color="inherit" /> : null }
                            sx={ { py: 1.25, textTransform: 'uppercase', fontWeight: 600 } }
                        >
                            { loading ? "Signing in…" : "Login" }
                        </Button>
                    </Stack>
                </Box>
            </Paper>
        </Box>
    );
}
